function trimString(value) {
    return String(value ?? "").trim();
}
function appendDescription(anchor, id) {
    const ids = trimString(anchor?.getAttribute?.("aria-describedby"))
        .split(/\s+/u)
        .filter(Boolean);
    if (!ids.includes(id)) {
        ids.push(id);
        anchor?.setAttribute?.("aria-describedby", ids.join(" "));
    }
}

function removeDescription(anchor, id) {
    const ids = trimString(anchor?.getAttribute?.("aria-describedby"))
        .split(/\s+/u)
        .filter((candidate) => candidate && candidate !== id);
    if (ids.length > 0) {
        anchor?.setAttribute?.("aria-describedby", ids.join(" "));
    } else {
        anchor?.removeAttribute?.("aria-describedby");
    }
}

function isWithin(container, target) {
    let current = target;
    while (current) {
        if (current === container) {
            return true;
        }
        current = current.parentElement ?? current.parentNode ?? null;
    }
    return false;
}

/**
 * Owns the shared hover/focus preview and optional click-pinned callout state machine.
 * Domain adapters only resolve anchors, render content, and position panels.
 */
export function attachAnchoredCalloutController(root, adapter) {
    if (
        !root
        || typeof root.addEventListener !== "function"
        || !adapter
        || typeof adapter.resolveAnchor !== "function"
        || typeof adapter.present !== "function"
    ) {
        return null;
    }

    let activeAnchor = null;
    let activeEvent = null;
    let activePresentation = null;
    let dismissedAnchor = null;
    let pinnedAnchor = null;
    let restoringFocusAnchor = null;
    const pinnable = adapter.pinnable !== false;

    const panel = () => activePresentation?.panel ?? adapter.panel?.() ?? null;
    const focusTarget = () => activePresentation?.focusTarget ?? panel();
    const targetIsWithinPanel = (target) => isWithin(panel(), target);

    const configureAnchor = (anchor) => {
        if (pinnable) {
            anchor?.setAttribute?.("aria-controls", adapter.id);
            anchor?.setAttribute?.("aria-haspopup", "dialog");
        }
        if (!anchor?.getAttribute?.("role") && adapter.defaultAnchorRole) {
            anchor?.setAttribute?.("role", adapter.defaultAnchorRole);
        }
        if (pinnable && anchor?.getAttribute?.("aria-expanded") === null) {
            anchor?.setAttribute?.("aria-expanded", "false");
        }
        adapter.configureAnchor?.(anchor);
    };

    const configureMode = (presentation, anchor, pinned) => {
        const callout = presentation.panel;
        const target = presentation.focusTarget ?? callout;
        configureAnchor(anchor);
        if (pinnable) {
            anchor.setAttribute?.("aria-expanded", pinned ? "true" : "false");
        }
        if (pinned) {
            callout.setAttribute?.("role", "dialog");
            callout.setAttribute?.("aria-modal", "false");
            callout.setAttribute?.(
                "aria-label",
                trimString(anchor.getAttribute?.("aria-label"))
                    || trimString(presentation.dialogLabel)
                    || adapter.fallbackDialogLabel,
            );
            if (adapter.pinnedAttribute) {
                callout.setAttribute?.(adapter.pinnedAttribute, "");
            }
            target?.setAttribute?.("tabindex", "0");
            return;
        }
        callout.setAttribute?.("role", "tooltip");
        callout.removeAttribute?.("aria-modal");
        callout.removeAttribute?.("aria-label");
        if (adapter.pinnedAttribute) {
            callout.removeAttribute?.(adapter.pinnedAttribute);
        }
        target?.setAttribute?.("tabindex", "-1");
    };

    const dismiss = ({ rememberAnchor = false, restoreFocus = false } = {}) => {
        const previousAnchor = activeAnchor;
        if (rememberAnchor && previousAnchor) {
            dismissedAnchor = previousAnchor;
        }
        if (previousAnchor) {
            removeDescription(previousAnchor, adapter.id);
            if (pinnable) {
                previousAnchor.setAttribute?.("aria-expanded", "false");
            }
        }
        const callout = panel();
        if (callout) {
            callout.hidden = true;
            if (adapter.pinnedAttribute) {
                callout.removeAttribute?.(adapter.pinnedAttribute);
            }
        }
        adapter.onDismiss?.();
        activeAnchor = null;
        activeEvent = null;
        activePresentation = null;
        pinnedAnchor = null;
        if (restoreFocus && typeof previousAnchor?.focus === "function") {
            restoringFocusAnchor = previousAnchor;
            previousAnchor.focus({ preventScroll: true });
            restoringFocusAnchor = null;
        }
    };

    const hide = (anchor = activeAnchor) => {
        if (pinnedAnchor) {
            return;
        }
        if (anchor && activeAnchor && anchor !== activeAnchor) {
            return;
        }
        dismiss();
    };

    const show = (anchor, event, { pinned = false } = {}) => {
        if (
            !anchor
            || (pinnedAnchor && pinnedAnchor !== anchor && !pinned)
            || (anchor === dismissedAnchor && !pinned)
        ) {
            return false;
        }
        const anchorChanged = activeAnchor !== anchor;
        const presentation = adapter.present(anchor, event, {
            anchorChanged,
            pinned,
        });
        if (!presentation?.panel) {
            dismiss();
            return false;
        }
        if (activeAnchor && activeAnchor !== anchor) {
            removeDescription(activeAnchor, adapter.id);
            if (pinnable) {
                activeAnchor.setAttribute?.("aria-expanded", "false");
            }
        }
        activeAnchor = anchor;
        activeEvent = event ?? activeEvent;
        activePresentation = presentation;
        appendDescription(anchor, adapter.id);
        if (pinned) {
            pinnedAnchor = anchor;
            dismissedAnchor = null;
        }
        configureMode(presentation, anchor, pinned);
        presentation.panel.hidden = false;
        adapter.position?.(presentation, anchor, event, {
            anchorChanged,
            pinned,
        });
        if (anchorChanged) {
            adapter.onAnchorChanged?.(presentation, anchor);
        }
        return true;
    };

    const togglePinned = (anchor, event, { focusPanel = false } = {}) => {
        if (pinnedAnchor === anchor) {
            dismiss({ rememberAnchor: true, restoreFocus: focusPanel });
            return;
        }
        if (!show(anchor, event, { pinned: true })) {
            return;
        }
        if (focusPanel) {
            focusTarget()?.focus?.({ preventScroll: true });
        }
    };

    const onMouseOver = (event) => {
        if (pinnedAnchor) {
            return;
        }
        const target = adapter.resolveAnchor(event.target);
        if (!target) {
            return;
        }
        if (target === dismissedAnchor && adapter.resolveAnchor(event.relatedTarget) !== target) {
            dismissedAnchor = null;
        }
        show(target, event);
    };

    const onMouseMove = (event) => {
        if (pinnedAnchor) {
            return;
        }
        const target = adapter.resolveAnchor(event.target);
        if (target) {
            show(target, event);
        }
    };

    const onMouseOut = (event) => {
        const target = adapter.resolveAnchor(event.target);
        if (target) {
            if (
                adapter.resolveAnchor(event.relatedTarget) !== target
                && !targetIsWithinPanel(event.relatedTarget)
            ) {
                hide(target);
            }
            return;
        }
        if (
            panel()
            && targetIsWithinPanel(event.target)
            && !targetIsWithinPanel(event.relatedTarget)
            && adapter.resolveAnchor(event.relatedTarget) !== activeAnchor
        ) {
            hide();
        }
    };

    const onFocusIn = (event) => {
        if (pinnedAnchor) {
            return;
        }
        const target = adapter.resolveAnchor(event.target);
        if (!target || target === restoringFocusAnchor) {
            return;
        }
        if (target === dismissedAnchor) {
            dismissedAnchor = null;
        }
        show(target);
    };

    const onFocusOut = (event) => {
        const target = adapter.resolveAnchor(event.target);
        if (
            target
            && adapter.resolveAnchor(event.relatedTarget) !== target
            && !targetIsWithinPanel(event.relatedTarget)
        ) {
            hide(target);
        }
    };

    const onPointerDown = (event) => {
        if (
            !pinnable
            || !pinnedAnchor
            || adapter.resolveAnchor(event.target)
            || targetIsWithinPanel(event.target)
        ) {
            return;
        }
        dismiss();
    };

    const onClick = (event) => {
        if (!pinnable) {
            return;
        }
        const target = adapter.resolveAnchor(event.target);
        if (target) {
            togglePinned(target, event, { focusPanel: event.detail === 0 });
        }
    };

    const onKeyDown = (event) => {
        if (event.key === "Escape" && activeAnchor && panel()?.hidden === false) {
            const restoreFocus = Boolean(pinnedAnchor);
            dismissedAnchor = activeAnchor;
            dismiss({ restoreFocus });
            return;
        }
        const target = adapter.resolveAnchor(event.target);
        const nativeButton = trimString(target?.tagName).toLowerCase() === "button";
        if (
            !pinnable
            || !target
            || nativeButton
            || (event.key !== "Enter" && event.key !== " " && event.key !== "Spacebar")
        ) {
            return;
        }
        event.preventDefault?.();
        togglePinned(target, event, { focusPanel: true });
    };

    root.addEventListener("mouseover", onMouseOver);
    root.addEventListener("mousemove", onMouseMove);
    root.addEventListener("mouseout", onMouseOut);
    root.addEventListener("focusin", onFocusIn);
    root.addEventListener("focusout", onFocusOut);
    root.addEventListener("pointerdown", onPointerDown, true);
    root.addEventListener("click", onClick);
    root.addEventListener("keydown", onKeyDown);

    return Object.freeze({
        dismissAnchor(anchor) {
            if (activeAnchor === anchor) {
                dismiss();
            }
        },
        dispose() {
            dismiss();
            root.removeEventListener?.("mouseover", onMouseOver);
            root.removeEventListener?.("mousemove", onMouseMove);
            root.removeEventListener?.("mouseout", onMouseOut);
            root.removeEventListener?.("focusin", onFocusIn);
            root.removeEventListener?.("focusout", onFocusOut);
            root.removeEventListener?.("pointerdown", onPointerDown, true);
            root.removeEventListener?.("click", onClick);
            root.removeEventListener?.("keydown", onKeyDown);
        },
        isActive(anchor) {
            return activeAnchor === anchor;
        },
        isPinned(anchor) {
            return pinnedAnchor === anchor;
        },
        refreshAnchor(anchor) {
            if (activeAnchor === anchor) {
                show(anchor, activeEvent, { pinned: pinnedAnchor === anchor });
            }
        },
    });
}
